import { create } from 'zustand';
import { api } from '../services/api';

export interface BinReminder {
  id: string;
  family_id: string;
  bin_type: string;
  colour: string;
  collection_day: string;
  frequency: string;
  start_date: string | null;
  reminder_time: string;
  remind_day_before: boolean;
  remind_on_day: boolean;
  notes: string;
  enabled: boolean;
  next_collection: string | null;
  created_by: string;
  created_at: string;
}

interface BinsState {
  bins: BinReminder[];
  isLoading: boolean;
  error: string | null;
  fetchBins: () => Promise<void>;
  createBin: (data: Partial<BinReminder>) => Promise<void>;
  updateBin: (id: string, data: Partial<BinReminder>) => Promise<void>;
  deleteBin: (id: string) => Promise<void>;
  handleWsMessage: (message: any) => void;
}

export const useBinsStore = create<BinsState>((set, get) => ({
  bins: [],
  isLoading: false,
  error: null,

  fetchBins: async () => {
    set({ isLoading: true, error: null });
    try {
      const res = await api.get('/bins');
      set({ bins: res.data.bins || [], isLoading: false });
    } catch (err: any) {
      set({ error: err.message, isLoading: false });
    }
  },

  createBin: async (data) => {
    try {
      const res = await api.post('/bins', data);
      set(state => ({ bins: [...state.bins, res.data.bin] }));
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },

  updateBin: async (id, data) => {
    try {
      const res = await api.put(`/bins/${id}`, data);
      set(state => ({ bins: state.bins.map(b => b.id === id ? res.data.bin : b) }));
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },

  deleteBin: async (id) => {
    try {
      await api.delete(`/bins/${id}`);
      set(state => ({ bins: state.bins.filter(b => b.id !== id) }));
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },

  handleWsMessage: (message) => {
    const { type, payload } = message;
    if (type === 'BIN_REMINDER_CREATED') {
      set(state => {
        if (state.bins.some(b => b.id === payload.id)) return state;
        return { bins: [...state.bins, payload] };
      });
    } else if (type === 'BIN_REMINDER_UPDATED') {
      set(state => ({ bins: state.bins.map(b => b.id === payload.id ? payload : b) }));
    } else if (type === 'BIN_REMINDER_DELETED') {
      set(state => ({ bins: state.bins.filter(b => b.id !== payload.binId) }));
    }
  },
}));
